import React from "react";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Spinner} from "../../common/ui/spinner/Spinner";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {ConfigurationSet, ConfigurationSets, NumberRange, ConfigurationSetDetail} from "../model";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  OverlayTrigger,
  Row,
  Tooltip
} from "react-bootstrap";
import {ActionBar} from "../../common/ui/actionbar/ActionBar";
import {ApolloApi} from "../../api/ApolloApi";
import {mapErrorToMessage} from "../../common/util/ErrorMapper";
import {ErrorDisplay} from "../../common/ui/errordisplay/ErrorDisplay";
import {DataSecurityDialog} from "../../data/DataSecurityDialog";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DropResult
} from "react-beautiful-dnd";
import * as mui from "@material-ui/core";
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  TextField
} from "@material-ui/core";
import DragHandleIcon from '@material-ui/icons/DragHandle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import {Autocomplete} from "@material-ui/lab";
import {LcdIcon} from "../../common/ui/icon/LcdIcon";
import {AccessRule} from "../../model";
import ReactDOM from "react-dom";
import axios, {AxiosError} from "axios";
import {CombinedCronPicker, Mode} from "../../common/ui/cron/CombinedCronPicker";
import {CronUtil, CronValidationResult} from "../../common/ui/cron/CronUtil";
import {convertSecurityFormAccessRulesToAccessRules, SecurityFormAccessRule} from "../../security/model";
import {Redirect} from "react-router-dom";

const LABEL_COL = 3;
const CONTROL_COL = 8;
const HELP_COL = 1;
const CONFIG_PAGE_MESSAGES = defineMessages({
  PARSERS: {
    id: "studio.settings.config.parsers",
    defaultMessage: "Parsers",
  },
  FILTERS: {
    id: "studio.settings.config.filters",
    defaultMessage: "Active Crawl Filters",
  },
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },
  SAVE: {
    id: "studio.settings.config.save-changes",
    defaultMessage: "You have unsaved changes. Do you wish to save them now?",
  }
});
const EXTRACT_REMOVE_EXPIRED_SCHEDULE = "desRemovedExpiredOutputJobSchedule";
const CRAWL_METADATA_PARSERS = "crawlMetadataParsers";
const CRAWL_ACCESS_RULES = "crawlAccessRules";
const CRAWL_FILTERS_ACTIVE = "crawlFiltersActive";
const CRAWL_FILTERS_AVAILABLE = "crawlFiltersAvailable";

interface ConfigPageState {
  page: string;
  loading: boolean;
  saving: boolean;
  configurationDescription: ConfigurationSets;
  selectedConfigDescription: ConfigurationSet;
  error: AxiosError;
  availableMetadataParsers: string[];
  crawlAccessDefaultRoles: AccessRule[];
  showDataSecurityDialog: boolean;
  spatialAreaCoordinates?: number[];
  previewMapDrawModeActive: boolean;
  previewMapEditModeActive: boolean;
  previewMapEditCoordinates: number[];
  activeCrawlFilters: string[];
  availableCrawlFilters: string[];
  professionalLicense: boolean;
  geoprocessingServiceUrlValid: boolean,
  geoprocessingServerUrl: string,
  desDeleteScheduleMode?: Mode,
}

class ConfigPageComponent extends React.Component<WithApiProperties & InjectedIntlProps, ConfigPageState> {
  private listItemPortalElement: HTMLDivElement;

  constructor(props) {
    super(props);
    this.state = {
      page: null,
      loading: true,
      saving: false,
      configurationDescription: null,
      selectedConfigDescription: null,
      error: null,
      availableMetadataParsers: [],
      activeCrawlFilters: [],
      availableCrawlFilters: [],
      crawlAccessDefaultRoles: [],
      showDataSecurityDialog: false,
      spatialAreaCoordinates: null,
      previewMapDrawModeActive: false,
      previewMapEditModeActive: false,
      previewMapEditCoordinates: null,
      professionalLicense: null,
      geoprocessingServiceUrlValid: true,
      geoprocessingServerUrl: null,
    };
  }

  componentDidUpdate() {
    const {configurationDescription, page} = this.state;

    if (configurationDescription === null) {
      return;
    }
    const {hash} = window.location;
    const newPage = hash.split("page=")[1];
    if (newPage !== page) {
      const configurationSet = configurationDescription.configurationSets.find(({id}) => id === newPage);
      this.setState({page: newPage, selectedConfigDescription: configurationSet, loading: true});
      ApolloApi.getStudioSettings()
          .then((r) => {
            configurationSet.details.forEach(detail => {
              detail.value = r.data.values[detail.configurationItemKey] !== undefined
                             ? r.data.values[detail.configurationItemKey] : "";
              detail.changed = false;
            });
            this.setState({loading: false});
          })
          .catch((error) => {
            this.setState({error});
          });
    }
  }

  handlePageChange = () => {
    const {intl} = this.props;

    if (this.valuesChanged()) {
      if (window.confirm(intl.formatMessage(CONFIG_PAGE_MESSAGES.SAVE))) {
        this.save();
      }
    }
    if (!window.location.hash.startsWith("#/settings/config?page=")) {
      window.removeEventListener("popstate", this.handlePageChange);
    }
  }

  componentDidMount() {
    window.addEventListener("popstate", this.handlePageChange);
    this.listItemPortalElement = document.createElement("div");
    this.listItemPortalElement.classList.add("crawl-data-dialog-list--item-portal");
    document.body.appendChild(this.listItemPortalElement);
    Promise.allSettled([
      ApolloApi.getStudioSettingsDescription(),
      ApolloApi.getMetadataParsers(),
      axios.get('../actuators/health/license'),
      this.props.api.getGeoprocessingServiceSettings(),
    ])
        .then((response) => {
          const rejected = response.filter((res) =>
              res.status === 'rejected' && res?.reason?.response.status === 401) as PromiseRejectedResult[];
          if (rejected.length > 0) {
            this.setState({error: rejected[0]?.reason});
            return;
          }
          const results = response.filter((res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];
          const configurationDescription = results[0].value;
          const availableMetadataParsers = results[1].value;
          const geoprocessingServerUrl = results[3].value.geoprocessingServerUrl;
          let professionalLicense = false;
          const licenseResponse = response[2] as PromiseFulfilledResult<any>;
          if (licenseResponse.value.status === 200 && licenseResponse.value.data.status &&
              licenseResponse.value.data.status === "UP" && licenseResponse.value.data?.details.feature) {
            professionalLicense = licenseResponse.value.data.details.feature === "approf";
          }

          if (geoprocessingServerUrl) {
            ApolloApi.isGeoprocessingServiceUrlValid(geoprocessingServerUrl)
                .then((geoprocessingServiceUrlValid) => {
                  this.setState({geoprocessingServiceUrlValid});
                })
                .catch(() => {
                  this.setState({geoprocessingServiceUrlValid: false});
                });
          }

          this.setState({
            configurationDescription,
            availableMetadataParsers,
            professionalLicense,
            geoprocessingServerUrl
          });
        })
        .catch((error) => {
          this.setState({error});
        });
  }

  renderContent() {
    const {page, professionalLicense, geoprocessingServiceUrlValid} = this.state;
    if (page === "Data%20Extraction" && (!professionalLicense || !geoprocessingServiceUrlValid)) {
      return this.renderDESError();
    } else {
      return this.renderItems();
    }
  }

  renderItems() {
    const details = this.state.selectedConfigDescription.details;
    const detailsElements: JSX.Element[] = [];

    details.forEach(detail => {
      if (detail.configurationItemKey !== CRAWL_FILTERS_AVAILABLE) {
        const control = this.getFormControl(detail);
        const element = this.getFormElement(detail, control);
        detailsElements.push(element);
      }
    });
    return detailsElements;
  }

  renderDESError() {
    const {professionalLicense, geoprocessingServiceUrlValid, geoprocessingServerUrl} = this.state;
    if (!professionalLicense) {
      return (
          <div>
            <div>
              <br/>
              <strong>
                <FormattedMessage id="studio.settings.config.not-professional"
                                  defaultMessage="ERDAS APOLLO Professional License is required for Data Extraction."/>
              </strong>
              <br/>
            </div>
          </div>
      );
    } else if (!geoprocessingServiceUrlValid) {
      return (
          <div>
            <div>
              <strong>
                <FormattedMessage id="studio.settings.geoprocessing-service.not-found"
                                  defaultMessage="ERDAS APOLLO Geoprocessing endpoint URL is not valid. Please check configuration."/>
              </strong>
              <br/><br/>
              <FormattedMessage id="studio.settings.geoprocessing-service.current"
                                defaultMessage="Current Geoprocessing Engine base URL is: "/>
              {geoprocessingServerUrl}
            </div>
          </div>
      );
    }
  }

  private getFormElement(detail, control) {
    const {intl} = this.props;

    return (<FormGroup key={`groupKey_${detail.configurationItemKey}`}
                       controlId={detail.configurationItemKey}>
      <Col componentClass={ControlLabel} sm={LABEL_COL}>
        {/* tslint:disable */}
        {/* Disabled because: Variables are not allowed for translation messages */}
        <FormattedMessage id={`studio.settings.config.${detail.configurationItemKey}.label`}
                          defaultMessage={`${detail.label}`}/>
        {/* tslint:enable */}
      </Col>
      <Col sm={CONTROL_COL}>
        {control}
      </Col>
      <Col sm={HELP_COL} className={"config-help-icon"}>
        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip_help">
          {/* tslint:disable */}
          {/* Disabled because: Variables are not allowed for translation messages */}
          <FormattedMessage id={`studio.settings.config.${detail.configurationItemKey}.help`}
                            defaultMessage={`${detail.description}`}/>
          {/* tslint:enable */}
        </Tooltip>}>
          <img src={`theme/assets/images/help_16.png`}
               alt={intl.formatMessage(CONFIG_PAGE_MESSAGES.HELP)}/>
        </OverlayTrigger>
      </Col>
    </FormGroup>);
  }

  private getFormControl(detail) {
    if (detail.configurationItemKey === EXTRACT_REMOVE_EXPIRED_SCHEDULE) {
      return (this.getDESDeleteScheduleControl(detail));
    } else if (detail.configurationItemKey === CRAWL_METADATA_PARSERS) {
      return (this.getMetadataParserControl(detail));
    } else if (detail.configurationItemKey === CRAWL_ACCESS_RULES) {
      return (this.getSecurityButtonControl(detail));
    } else if (detail.configurationItemKey === CRAWL_FILTERS_ACTIVE) {
      return (this.getActiveFiltersControl(detail));
    } else {
      if (detail.type === "READONLY") {
        return (this.getReadOnlyControl(detail));
      }
      if (["DOUBLE", "LONG"].includes(detail.type)) {
        return (this.getNumberControl(detail));
      }
      if (detail.type === "BOOLEAN") {
        return (this.getCheckboxControl(detail));
      }
      if (detail.type === "STRING" && detail.range !== undefined) {
        return (this.getSelectControl(detail));
      }
    }
    return (<FormControl key={`controlKey_${detail.configurationItemKey}`}
                         name={detail.configurationItemKey}
                         value={detail.value}
                         onChange={this.onChange}
                         alt={detail.description}/>);
  }

  private getReadOnlyControl(detail) {
    return (<div className={"config-read-only"}>{detail.value}</div>);
  }

  private getCheckboxControl(detail) {
    return (<Checkbox key={`chkKey_${detail.configurationItemKey}`}
                      inline={true}
                      name={detail.configurationItemKey}
                      checked={detail.value === "true"}
                      onChange={this.onChange}/>);
  }

  private getSelectControl(detail) {
    const options: JSX.Element[] = [];
    const values = detail.range.substring(1, detail.range.length - 1).split(",");
    values.forEach((value) => {
      const option = <option key={`optKey_${value}`} value={value}>{value}</option>;
      options.push(option);
    });
    return (
        <FormControl key={`controlKey_${detail.configurationItemKey}`}
                     name={detail.configurationItemKey}
                     value={detail.value}
                     componentClass="select"
                     onChange={this.onChange}>
          {options}
        </FormControl>);
  }

  private getNumberControl(detail) {
    const props = {
      type: "number",
      key: `controlKey_${detail.configurationItemKey}`,
      name: detail.configurationItemKey,
      value: detail.value,
      onChange: this.onChange,
      alt: detail.description,
      className: "form-control",
    };
    if (detail.range !== undefined) {
      try {
        const range: NumberRange = JSON.parse(detail.range);
        if (range?.minimum) {
          props["min"] = range.minimum;
        }
        if (range?.maximum) {
          props["max"] = range.maximum;
        }
      } catch (error) {
        this.setState({error});
      }
    }
    return (React.createElement("input", props));
  }

  private getSecurityButtonControl(detail) {
    return (<Button key={`chkKey_${detail.configurationItemKey}`}
                    onClick={this.openDataSecurityDialog}
                    data-cy="data-security-button"
                    name={detail.configurationItemKey}>
      <LcdIcon icon="lock-fill"/>
      <FormattedMessage id="studio.data-security-dialog.title" defaultMessage="Security"/>
    </Button>);
  }

  openDataSecurityDialog = (): void => {
    this.setState({showDataSecurityDialog: true});
  }

  closeDataSecurityDialog = (): void => {
    this.setState({showDataSecurityDialog: false});
  }

  updateCrawlAccessSettings = (crawlDefaultAccessRules: SecurityFormAccessRule[]): void => {
    this.setState(prevState => {
      const selectedConfigDescription = prevState.selectedConfigDescription;
      const detail = selectedConfigDescription.details.find(
          ({configurationItemKey}) => configurationItemKey === CRAWL_ACCESS_RULES);
      detail.value = JSON.stringify(crawlDefaultAccessRules);
      detail.changed = true;

      return {selectedConfigDescription};
    });
  }

  validateSchedule = (cronStringToValidate: string): Promise<CronValidationResult> => {
    const validate = (validateCRON) => Promise.resolve(validateCRON);
    return validate(CronUtil.validateCronString(cronStringToValidate, this.props.intl));
  }

  switchMode = (newMode) => {
    this.setState({desDeleteScheduleMode: newMode});
  }

  getDESDeleteScheduleControl(schedule) {
    const {desDeleteScheduleMode} = this.state;
    let mode: Mode = desDeleteScheduleMode;
    if (!desDeleteScheduleMode) {
      if (schedule.value) {
        mode = CombinedCronPicker.canUseSimpleMode(schedule.value, this.props.intl) ? "simple" : "advanced";
      } else {
        mode = "notset";
      }
    }

    return <CombinedCronPicker value={schedule.value}
                               intl={this.props.intl}
                               disableNotset={true}
                               validate={this.validateSchedule}
                               onModeSwitch={this.switchMode}
                               mode={mode}
                               onChange={(value) => this.onChange({
                                 target: {
                                   name: EXTRACT_REMOVE_EXPIRED_SCHEDULE,
                                   value
                                 }
                               })}/>;
  }

  private getMetadataParserControl(aActiveMetadataParsers) {
    const {intl} = this.props;
    const {availableMetadataParsers} = this.state;

    const indexesOfActiveParsersIntoListOfAvailableParsers = [];
    if (typeof (aActiveMetadataParsers.value) === "string") {
      //JSON string to array of string indexes
      const listOfActiveParsers = JSON.parse(aActiveMetadataParsers.value);
      listOfActiveParsers.forEach((parser) => {
        const index = availableMetadataParsers.indexOf(parser);
        if (index >= 0) {
          indexesOfActiveParsersIntoListOfAvailableParsers.push(index.toString());
        }
      })
    }

    return (<div>
      <Autocomplete
          data-cy="parsers"
          multiple
          limitTags={1}
          options={Object.keys(availableMetadataParsers)}
          getOptionLabel={(option) => availableMetadataParsers[option] as string}
          value={indexesOfActiveParsersIntoListOfAvailableParsers}
          disableCloseOnSelect
          onChange={(_event, value) => this.onChange({
            target: {
              name: aActiveMetadataParsers.configurationItemKey,
              value
            }
          })}
          renderOption={(option) => (
              <React.Fragment>
                <mui.Checkbox checked={indexesOfActiveParsersIntoListOfAvailableParsers.includes(option)}/>
                {availableMetadataParsers[option] as string}
              </React.Fragment>
          )}
          renderInput={(params) => (
              <TextField {...params} variant="outlined"
                         placeholder={intl.formatMessage(CONFIG_PAGE_MESSAGES.PARSERS)}/>
          )}

      />

      <DragDropContext onDragEnd={this.onMetadataParsersDragEnd}>
        <Droppable droppableId="droppable" direction="vertical">
          {(providedDroppable) => (
              <div ref={providedDroppable.innerRef}>
                <List>
                  {indexesOfActiveParsersIntoListOfAvailableParsers.map((item, index) => (
                      <Draggable key={item} draggableId={item} index={index}>
                        {(provided, snapshot) =>
                            this.renderMetadataParsersListItem(provided, snapshot, item)
                        }
                      </Draggable>
                  ))}
                  {providedDroppable.placeholder}
                </List>
              </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>);
  }

  private renderMetadataParsersListItem(provided: DraggableProvided, snapshot: DraggableStateSnapshot,
                                        parserId: string) {
    const {availableMetadataParsers} = this.state;
    const listItem = <div ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={ConfigPageComponent.getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                          )}><ListItem>
      <ListItemIcon>
        <DragHandleIcon/>
      </ListItemIcon>
      <ListItemText
          primary={availableMetadataParsers[parserId]}
      />
      <ListItemSecondaryAction>
        <IconButton onClick={this.onMetadataParserRemove.bind(this, parserId)}>
          <HighlightOffIcon/>
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
    </div>;

    if (snapshot.isDragging) {
      return ReactDOM.createPortal(<List>{listItem}</List>, this.listItemPortalElement);
    }

    return listItem;
  }

  private onMetadataParserRemove = (parserId: string) => {
    this.setState(prevState => {
      const {availableMetadataParsers, selectedConfigDescription} = prevState;
      const detail = selectedConfigDescription.details.find(
          ({configurationItemKey}) => configurationItemKey === CRAWL_METADATA_PARSERS);

      const list = JSON.parse(detail.value);
      const index = list.indexOf(availableMetadataParsers[parserId]);
      if (index !== -1) {
        list.splice(index, 1);
      }
      detail.value = JSON.stringify(list);
      detail.changed = true;
      return {selectedConfigDescription};
    });
  }

  private onMetadataParsersDragEnd = (result: DropResult) => {
    if (typeof result.destination?.index !== "number") {
      return;
    }

    this.setState(prevState => {
      const selectedConfigDescription = prevState.selectedConfigDescription;
      const detail = selectedConfigDescription.details.find(
          ({configurationItemKey}) => configurationItemKey === CRAWL_METADATA_PARSERS);

      const reorderedList = JSON.parse(detail.value);
      const [removed] = reorderedList.splice(result.source.index, 1);
      reorderedList.splice(result.destination.index, 0, removed);
      detail.value = JSON.stringify(reorderedList);

      detail.changed = true;
      return {selectedConfigDescription};
    });

  }

  private getAvailableCrawlFilters() {
    return JSON.parse(this.state.configurationDescription.configurationSets
        .filter(o => {
          return o.id === "Crawling%20Defaults"
        })[0].details
        .filter(o => {
          return o.configurationItemKey === CRAWL_FILTERS_AVAILABLE
        })[0].value);
  }

  private getActiveFiltersControl(aActiveCrawlFilters) {
    const {intl} = this.props;
    const availableCrawlFilters = this.getAvailableCrawlFilters();

    const indexesOfActiveCrawlFiltersIntoListOfAvailableFilters = [];
    if (typeof (aActiveCrawlFilters.value) === "string") {
      //JSON string to array of string indexes
      const listOfActiveFilters = JSON.parse(aActiveCrawlFilters.value);
      listOfActiveFilters.forEach((filter) => {
        const index = availableCrawlFilters.indexOf(filter);
        if (index >= 0) {
          indexesOfActiveCrawlFiltersIntoListOfAvailableFilters.push(index.toString());
        }
      })
    }

    return (<div>
      <Autocomplete
          data-cy="filters"
          multiple
          limitTags={1}
          options={Object.keys(availableCrawlFilters)}
          getOptionLabel={o => availableCrawlFilters[o] as string}
          value={indexesOfActiveCrawlFiltersIntoListOfAvailableFilters}
          disableCloseOnSelect
          onChange={(_event, value) => this.onChange({
            target: {
              name: aActiveCrawlFilters.configurationItemKey,
              value
            }
          })}
          renderOption={(option) => (
              <React.Fragment>
                <mui.Checkbox checked={indexesOfActiveCrawlFiltersIntoListOfAvailableFilters.includes(option)}/>
                {availableCrawlFilters[option] as string}
              </React.Fragment>
          )}
          renderInput={(params) => (
              <TextField {...params} variant="outlined"
                         placeholder={intl.formatMessage(CONFIG_PAGE_MESSAGES.FILTERS)}/>
          )}

      />

      <DragDropContext onDragEnd={this.onCrawlFiltersDragEnd}>
        <Droppable droppableId="droppable" direction="vertical">
          {(providedDroppable) => (
              <div ref={providedDroppable.innerRef}>
                <List>
                  {indexesOfActiveCrawlFiltersIntoListOfAvailableFilters.map((item, index) => (
                      <Draggable key={item} draggableId={item} index={index}>
                        {(provided, snapshot) =>
                            this.renderCrawlFiltersListItem(provided, snapshot, item)
                        }
                      </Draggable>
                  ))}
                  {providedDroppable.placeholder}
                </List>
              </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>);
  }

  private renderCrawlFiltersListItem(provided: DraggableProvided, snapshot: DraggableStateSnapshot, parserId: string) {
    const availableCrawlFilters = this.getAvailableCrawlFilters();
    const listItem = <div ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={ConfigPageComponent.getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                          )}><ListItem>
      <ListItemIcon>
        <DragHandleIcon/>
      </ListItemIcon>
      <ListItemText
          primary={availableCrawlFilters[parserId]}
      />
      <ListItemSecondaryAction>
        <IconButton onClick={this.onCrawlFiltersRemove.bind(this, parserId)}>
          <HighlightOffIcon/>
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
    </div>;

    if (snapshot.isDragging) {
      return ReactDOM.createPortal(<List>{listItem}</List>, this.listItemPortalElement);
    }

    return listItem;
  }

  private onCrawlFiltersDragEnd = (result: DropResult) => {
    if (typeof result.destination?.index !== "number") {
      return;
    }

    this.setState(prevState => {
      const selectedConfigDescription = prevState.selectedConfigDescription;
      const detail = selectedConfigDescription.details.find(
          ({configurationItemKey}) => configurationItemKey === CRAWL_FILTERS_ACTIVE);

      const reorderedList = JSON.parse(detail.value);
      const [removed] = reorderedList.splice(result.source.index, 1);
      reorderedList.splice(result.destination.index, 0, removed);
      detail.value = JSON.stringify(reorderedList);
      detail.changed = true;
      return {selectedConfigDescription};
    });
  }

  private onCrawlFiltersRemove = (parserId: string) => {
    this.setState(prevState => {
      const availableCrawlFilters = this.getAvailableCrawlFilters();
      const selectedConfigDescription = prevState.selectedConfigDescription;
      const detail = selectedConfigDescription.details.find(
          ({configurationItemKey}) => configurationItemKey === CRAWL_FILTERS_ACTIVE);

      const list = JSON.parse(detail.value);
      const index = list.indexOf(availableCrawlFilters[parserId]);
      if (index !== -1) {
        list.splice(index, 1);
      }
      detail.value = JSON.stringify(list);

      detail.changed = true;
      return {selectedConfigDescription};
    });
  }

  private validateNumberRange = (detail: ConfigurationSetDetail, sValue: string): string => {
    try {
      const range: NumberRange = JSON.parse(detail.range);
      const value = Number(sValue);
      if (sValue !== "" && typeof value === "number") {
        if ((range?.exclusiveMinimum && value < range?.minimum) || (value <= range?.minimum)) {
          let newValue = range.minimum;
          if (range?.exclusiveMinimum) {
            newValue = (detail.type === "DOUBLE") ? newValue + 0.1 : newValue + 1;
          }
          return newValue.toString();
        }
        if ((range?.exclusiveMaximum && value > range?.maximum) || (value >= range?.maximum)) {
          let newValue = range.maximum;
          if (range?.exclusiveMaximum) {
            newValue = (detail.type === "DOUBLE") ? newValue - 0.1 : newValue - 1;
          }
          return newValue.toString();
        }
      } else if (detail?.default) {
        return detail.default;
      }
    } catch (error) {
      this.setState({error});
    }
    return sValue;
  }

  private onChange = (event: any) => {
    const {availableMetadataParsers} = this.state;
    const selectedConfigDescription = this.state.selectedConfigDescription;
    const detail: ConfigurationSetDetail = selectedConfigDescription.details.find(
        ({configurationItemKey}) => configurationItemKey === event.target.name);

    if (typeof (event.target.value) === "object" && event.target.name === CRAWL_METADATA_PARSERS) {
      const newList = [];
      event.target.value.forEach((parser) => {
        newList.push(availableMetadataParsers[parser]);
      });
      detail.value = JSON.stringify(newList);
    }

    if (typeof (event.target.value) === "object" && event.target.name === CRAWL_FILTERS_ACTIVE) {
      const availableCrawlFilters = this.getAvailableCrawlFilters();
      const newList = [];
      event.target.value.forEach((filter) => {
        newList.push(availableCrawlFilters[filter]);
      });
      detail.value = JSON.stringify(newList);
    }

    if (detail !== undefined) {
      if (event.target.name !== CRAWL_METADATA_PARSERS && event.target.name !== CRAWL_FILTERS_ACTIVE) {
        detail.value = (detail.type === "BOOLEAN") ? event.target.checked.toString() : event.target.value;
      }
      if (detail?.range) {
        detail.value = this.validateNumberRange(detail, event.target.value);
      }
      detail.changed = true;
      this.setState({selectedConfigDescription});
    }
  };

  private static getItemStyle(isDragging, draggableStyle) {
    return {
      ...draggableStyle,
      ...(isDragging && {
        background: "rgb(235,235,235)",
      }),
    };
  }

  private saveEnabled = (): boolean => {
    let saveEnabled = false;
    const selectedConfigDescription = this.state.selectedConfigDescription;
    if (selectedConfigDescription) {
      selectedConfigDescription.details.forEach(detail => {
        if (!saveEnabled && detail.type !== "READONLY") {
          saveEnabled = true;
        }
      });
    }
    return saveEnabled;
  }

  private valuesChanged = (): boolean => {
    let valuesChanges = false;
    const selectedConfigDescription = this.state.selectedConfigDescription;
    if (selectedConfigDescription) {
      selectedConfigDescription.details.forEach(detail => {
        if (detail.changed !== undefined && detail.changed) {
          valuesChanges = true;
        }
      });
    }
    return valuesChanges;
  }

  private save = () => {
    const changedValues = {values: {}};
    let valuesChanged = false;
    const selectedConfigDescription = this.state.selectedConfigDescription;
    selectedConfigDescription.details.forEach(detail => {
      if (detail.changed !== undefined && detail.changed) {
        if (detail.configurationItemKey === CRAWL_ACCESS_RULES) {
          changedValues.values[detail.configurationItemKey] =
              JSON.stringify(
                  convertSecurityFormAccessRulesToAccessRules(
                      JSON.parse(detail.value) as SecurityFormAccessRule[]
                  )
              );
        } else {
          changedValues.values[detail.configurationItemKey] = detail.value;
        }
        valuesChanged = true;
      }
    });
    if (valuesChanged) {
      this.setState({saving: true});
      ApolloApi.saveStudioSettings(changedValues)
          .then(() => {
            selectedConfigDescription.details.forEach(detail => {
              detail.changed = false;
            });
            this.setState({saving: false});
          })
          .catch((error) => {
            this.setState({saving: false, error});
            throw error;
          });
    }
  }

  private static renderSaving() {
    return (
        <Row style={{textAlign: "center"}}>
          <h2>
            <FormattedMessage id="studio.settings.general.saving"
                              defaultMessage="Saving..."/>
          </h2>
          <Spinner bigStyle/>
        </Row>
    );
  }

  private renderLoading() {
    return (
        <Row style={{textAlign: "center"}}>
          <h2>
            <FormattedMessage id="studio.settings.general.loading"
                              defaultMessage="Loading..."/>
          </h2>
          <Spinner bigStyle/>
        </Row>
    );
  }

  private renderError() {
    const {error} = this.state;
    const errorMessage = mapErrorToMessage(error);
    if (errorMessage) {
      return <Alert bsStyle="danger">{errorMessage as string}</Alert>;
    }
    return <ErrorDisplay error={error}/>;
  }

  componentWillUnmount() {
    document.body.removeChild(this.listItemPortalElement);
  }

  render() {
    const {
      selectedConfigDescription,
      showDataSecurityDialog,
      loading,
      saving,
      error,
      page,
      professionalLicense,
      geoprocessingServiceUrlValid,
      spatialAreaCoordinates
    } = this.state;

    if (error) {
      if (error?.response?.status === 401) {
        return <Redirect to="/login"/>;
      }
      return this.renderError();
    }

    if (loading) {
      return this.renderLoading();
    }

    if (saving) {
      return ConfigPageComponent.renderSaving();
    }

    return (<div>
          <h1>
            {/* tslint:disable */}
            {/* Disabled because: Variables are not allowed for translation messages */}
            <FormattedMessage id={`studio.settings.config.${selectedConfigDescription.id.replace(/%20/g, '')}.name`}
                              defaultMessage={`${selectedConfigDescription.name}`}/>
            {/* tslint:enable */}
          </h1>
          <hr/>
          <p>
            {/* tslint:disable */}
            {/* Disabled because: Variables are not allowed for translation messages */}
            <FormattedMessage
                id={`studio.settings.config.${selectedConfigDescription.id.replace(/%20/g, '')}.description`}
                defaultMessage={`${selectedConfigDescription.description}`}/>
            {/* tslint:enable */}
          </p>
          <Form horizontal className="settings-form">
            <div className="settings-fields-panel">
              {this.renderContent()}
              {showDataSecurityDialog && <DataSecurityDialog key={`data-security-dialog`}
                                                             previewMapDrawModeActive={this.state.previewMapDrawModeActive}
                                                             previewMapEditModeActive={this.state.previewMapEditModeActive}
                                                             onHide={this.closeDataSecurityDialog}
                                                             dataId={""}
                                                             dataType={"none"}
                                                             onUpdateDefaultRoles={this.updateCrawlAccessSettings}
                                                             spatialAreaCoordinates={spatialAreaCoordinates}
              />}
            </div>
            {!(page === "Data%20Extraction" && (!professionalLicense || !geoprocessingServiceUrlValid)) &&
             this.saveEnabled() &&
             <FormGroup>
               <Col smOffset={4} sm={4}>
                 <ActionBar>
                   <Button onClick={this.save} disabled={!this.valuesChanged()}>
                     <FormattedMessage id="studio.settings.general.save" defaultMessage="Save"/>
                   </Button>
                 </ActionBar>
               </Col>
             </FormGroup>
            }
          </Form>
        </div>
    );
  }
}

export const ConfigPage = injectIntl(WithApi(ConfigPageComponent));